import { useContext } from 'react'

import {
  Button, Form, ModalContext, SidebarModal,
} from '@domain-group/suit-theme'
import type { SidebarModalProps } from '@domain-group/suit-theme'

import { digObject } from '@domain-group/javascript-utils'

import type { HandleCallbackActionParams } from '@functions/handleCallbackAction'

import type { UseGeoSuburbPayload } from '@hooks/useGeoSuburb'
import useGeoSuburb, { useGeoSuburbForm } from '@hooks/useGeoSuburb'

import type { GeoSuburbModel } from '@models/types'

import type { ActionResult } from '@redux/modules/types'

import useCreateOrEditSuburbModal from './hooks/useCreateOrEditSuburbModal'

interface ModalCallbacks {
  closeModal: () => void,
  createGeoSuburb: (payload: HandleCallbackActionParams) => Promise<ActionResult<GeoSuburbModel>>,
  updateGeoSuburb: (payload: HandleCallbackActionParams) => Promise<ActionResult<GeoSuburbModel>>,
}

type CreateOrUpdateGeoSuburbParams = {
  entityState: Partial<GeoSuburbModel>,
  callbacks: ModalCallbacks,
  createFn: UseGeoSuburbPayload['callbacks']['createGeoSuburb'],
  updateFn: UseGeoSuburbPayload['callbacks']['updateGeoSuburb'],
}

const createOrUpdateGeoSuburb = (params: CreateOrUpdateGeoSuburbParams) => {
  const {
    entityState, callbacks, createFn, updateFn,
  } = params

  const { closeModal, createGeoSuburb, updateGeoSuburb } = callbacks || {}

  const [text, actionFn] = entityState.id ? ['Updated', updateFn] : ['Created', createFn]

  const geoSuburbPayload = {
    callbacks: {
      action: actionFn,
      afterAction: closeModal,
    },
    entityParams: entityState,
    toastText: `Suburb ${text}`,
  }

  // @ts-expect-error TS2345
  if (entityState.id) return updateGeoSuburb(geoSuburbPayload)

  // @ts-expect-error TS2345
  return createGeoSuburb(geoSuburbPayload)
}

type CreateOrEditSuburbModalProps = {
  callbacks: {
    createGeoSuburb: (payload: HandleCallbackActionParams) => Promise<ActionResult<GeoSuburbModel>>,
    updateGeoSuburb: (payload: HandleCallbackActionParams) => Promise<ActionResult<GeoSuburbModel>>,
  },
}

type ModalProps = {
  callbacks: ModalCallbacks,
  modalKey?: string,
  showModal: boolean,
}

const CreateOrEditSuburbModal = (props: ModalProps) => {
  const { callbacks, modalKey = 'CreateOrEditSuburbModal', showModal } = props

  const modalContext = useContext(ModalContext)
  const { modalData } = modalContext

  const modalPayload = digObject(modalData, modalKey, {})
  const { geoSuburb: initGeoSuburb } = modalPayload

  const geoSuburbPayload = useGeoSuburb(initGeoSuburb)
  const {
    callbacks: {
      createGeoSuburb: createFn,
      updateGeoSuburb: updateFn,
    },
    creating,
    geoSuburb,
    geoState,
    updating,
  } = geoSuburbPayload

  const geoSuburbFormPayload = useGeoSuburbForm(geoSuburb, { validateOn: 'change' })
  const {
    entityState,
    entityState: {
      active,
      geo_state_id,
      id,
      latitude,
      longitude,
      post_code,
      title,
    },
    handlers,
    saveEnabled,
  } = geoSuburbFormPayload

  // @ts-expect-error TS2322
  const useCreateOrEditSuburbModalPayload = useCreateOrEditSuburbModal({ geoSuburbFormPayload, geoState })
  const {
    callbacks: {
      handleCountryState,
    },
    countryCode,
    filteredGeoStates,
    geoCountries,
  } = useCreateOrEditSuburbModalPayload

  const updateOrCreateButtonText = id ? 'Save Suburb' : 'Create Suburb'

  return (
    <SidebarModal callbacks={callbacks} clickSafeZone modalKey={modalKey} showModal={showModal}>
      <SidebarModal.Header callbacks={callbacks} title={id ? 'Edit' : 'Add'} titleSecondLine="Suburb" />

      <SidebarModal.Content>
        <Form>
          <Form.Field label="* Country">
            <select
              onChange={e => handleCountryState(e.target.value)}
              value={countryCode}
            >
              <option value="">Please Select...</option>
              {Object.values(geoCountries).map(geoCountry => (
                <option key={geoCountry.id} value={geoCountry.code}>
                  {geoCountry.title}
                </option>
              ))}
            </select>
          </Form.Field>

          <Form.Field label="* State" marginTop="large">
            {/*
             // @ts-expect-error TS2322 */}
            <select
              data-value-type="number"
              name="geo_state_id"
              value={geo_state_id}
              {...handlers}
            >
              <option value="">Please Select...</option>
              {Object.values(filteredGeoStates).map(geoState => (
                <option key={geoState.id} value={geoState.id}>
                  {geoState.code}
                </option>
              ))}
            </select>
          </Form.Field>

          <Form.Field label="* Suburb Title" marginTop="large">
            <input
              name="title"
              value={title}
              type="text"
              {...handlers}
            />
          </Form.Field>

          <Form.Field label="* Postcode" marginTop="large">
            <input
              name="post_code"
              value={post_code}
              type="text"
              {...handlers}
            />
          </Form.Field>

          <Form.Field label="* Latitude" marginTop="large">
            <input
              name="latitude"
              step=".0001"
              type="number"
              value={latitude}
              {...handlers}
            />
          </Form.Field>

          <Form.Field label="* Longitude" marginTop="large">
            <input
              name="longitude"
              step=".0001"
              type="number"
              value={longitude}
              {...handlers}
            />
          </Form.Field>

          <Form.Field label="* Status" marginTop="large">
            {/*
             // @ts-expect-error TS2322 */}
            <select
              data-value-type="boolean"
              name="active"
              value={active}
              {...handlers}
            >
              <option value="true">Active</option>
              <option value="false">Inactive</option>
            </select>
          </Form.Field>
        </Form>
      </SidebarModal.Content>

      <SidebarModal.Footer>
        <Button
          buttonStyle="primaryCreate"
          disabled={!saveEnabled}
          loading={creating || updating}
          onClick={() => createOrUpdateGeoSuburb({
            entityState, callbacks, createFn, updateFn,
          })}
          size="large"
        >
          {saveEnabled ? updateOrCreateButtonText : 'Complete Fields'}
        </Button>
      </SidebarModal.Footer>
    </SidebarModal>
  )
}

const LazyLoadedModal = (props: SidebarModalProps & CreateOrEditSuburbModalProps) => (
  <SidebarModal.RenderController {...props}>
    <CreateOrEditSuburbModal {...props} />
  </SidebarModal.RenderController>
)

export default LazyLoadedModal
